import requests, { setToken } from "../../services/httpServices";
import { showErrorAlert } from "./AlertService";

const AdminServices = {
  getVisitorAnalytics: async (params) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.get("/admin/visitor-analytics", { params });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  getAllSchools: async (params) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);
      const response = await requests.get("/admin/get-all-schools", { params });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  getAllTeachers: async (params) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);
      const response = await requests.get("/admin/get-all-teachers", {
        params,
      });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  getSchoolTeachers: async (param) => {
    try {
      // Don't make API call if school_id is missing or undefined
      if (!param.school_id || param.school_id === 'undefined') {
        return { teachers: [], message: "No school selected." };
      }
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);
      const response = await requests.get(
        "/admin/get-all-school-teachers?school_id=" + param.school_id,
        { param }
      );
      return response;
    } catch (error) {
      // Only show error alert if it's not a "no school selected" case
      if (error.message && !error.message.includes("No school selected")) {
        showErrorAlert(error);
      }
      throw error;
    }
  },
  getAllInfluences: async () => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);
      const response = await requests.get("/admin/get-all-influencers");
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  getAllGrantApplications: async () => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);
      const response = await requests.get("/admin/get-all-grant-applications");
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  changeAdminPassword: async (changeData) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);

      const response = await requests.post("/admin/change-password", changeData);
      if (response.success) return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  updateTeacher: async (teacherData) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.put(`/admin/update-teacher`, teacherData);
      return response;
    } catch (error) {
      showErrorAlert(error);
    }
  },
  updateSchool: async (schoolData) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.put(`/admin/update-school`, schoolData);
      return response;
    } catch (error) {
      showErrorAlert(error);
    }
  },
  /**
   * Reclassify a school between the dashboard tabs (Paid / Free / Accredited /
   * Pending / Test).
   *
   * This lives here, in the copy under components/admin, because that is the one
   * schoolList/index.jsx imports ("../AdminServices"). An identical twin exists at
   * services/AdminServices.jsx and already had this method — which is exactly why
   * the dropdown appeared to be wired up and had in fact never worked: the call
   * resolved to undefined and threw a TypeError before any request was made, which
   * the caller reported as "could not reach the server".
   *
   * Errors propagate deliberately (no showErrorAlert) so the caller can toast the
   * real reason — 403 "super admin required", 404, and so on.
   */
  setCustomerStatus: async (schoolId, customerStatus) => {
    const token = localStorage.getItem("token");
    setToken(token);
    const response = await requests.put(`/admin/set-customer-status`, { schoolId, customerStatus });
    return response;
  },
  deleteSchool: async (schoolId) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);

      const response = await requests.delete(
        `/admin/delete-school/${schoolId}`
      );

      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  deleteGrantApplication: async (grantId) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);

      const response = await requests.delete(
        `/admin/delete-grant-application/${grantId}`
      );

      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },
  resetUserPassword: async (resetData) => {
    try {
      const token = localStorage.getItem("token"); // Get token from localStorage
      setToken(token);

      const response = await requests.post("/admin/admin-reset-password", resetData);
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  resendTempPassword: async (userData) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.post("/admin/resend-temp-password", userData);
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  // Drawing Metrics Services
  getDrawingMetrics: async (params) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.get("/admin/drawing-metrics", { params });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  getDrawingWinners: async () => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.get("/admin/drawing-winners");
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  selectDrawingWinner: async (winnerData) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.post("/admin/select-drawing-winner", winnerData);
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  exportDrawingData: async (params) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.get("/admin/export-drawing-data", { params });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  getDrawingAnalytics: async (params) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.get("/admin/drawing-analytics", { params });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  updateDrawingWinner: async (winnerId, updateData) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.put(`/admin/drawing-winner/${winnerId}`, updateData);
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  deleteDrawingWinner: async (winnerId) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.delete(`/admin/drawing-winner/${winnerId}`);
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  // Drawing Entries Services
  getDrawingEntries: async (params) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.get("/monthly-drawing/admin/entries", { params });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  // Search users by name or email (for typeahead)
  searchUsers: async (query) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.get("/admin/search-users", { params: { query } });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  // Add an ADDITIONAL school admin — demotes nobody.
  addSchoolAdmin: async ({ schoolId, userId, moveFromOtherSchool = false }) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.post("/admin/add-school-admin", { schoolId, userId, moveFromOtherSchool });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  // TRANSFER the school admin role — DEMOTES every existing admin on that school.
  changeSchoolAdmin: async ({ schoolId, newAdminUserId }) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.put("/admin/change-school-admin", { schoolId, newAdminUserId });
      return response;
    } catch (error) {
      showErrorAlert(error);
      throw error;
    }
  },

  // Create a demo school account (school + admin user + active payment)
  createDemoSchool: async (formData) => {
    try {
      const token = localStorage.getItem("token");
      setToken(token);
      const response = await requests.post("/admin/create-demo-school", formData);
      return response;
    } catch (error) {
      // Don't auto-show error alert — let the modal handle it
      throw error;
    }
  },
};


export default AdminServices;
